import { produce } from 'immer';
import { isNil } from 'lodash-es';
import { AnyAction } from 'redux';
import { Optional } from '../../../../../lib/types/Optional';
import { PaginationTokens } from '../../../../cross-cutting-concerns/state-management/interfaces/StateManagement.types';
import { Machine, MachineUpdateResponse } from '../../interfaces/Machine.types';
import { Reminder } from '../../../reminder/interfaces/Reminder.types';
import {
  DEFAULT_MACHINE_NOTIFICATIONS_PAGE_SIZE_VALUE,
  MACHINE_ATTACHMENT_LIST_PAGE_SIZE_LARGE,
  MACHINE_REMINDER_LIST_PAGE_SIZE,
} from '../../utils/constants';
import { MachineUtils } from '../../utils/MachineUtils';
import { IMachineAttachment, MachineAttachmentsListResponse } from '../../interfaces/MachineAttachment.types';
import type { EquipmentPositionSummary } from '../../interfaces/MachinePosition.types';
import type { MachineClientListPositionHistoriesOptions } from '../../MachineClient';
import { MachineDetailsPanelActions } from './machineDetailsPanelActions';
import {
  Notification,
  SortOrders,
  OperatingTimePeriod,
  MachineNote,
  Maybe,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { DEFAULT_PAGE_VALUE } from 'config/constants';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import { getNextPaginationToken } from 'app/utils/infinite-scroll/infiniteScroll';
import { sortPositionSummariesByEnteredAtDesc } from 'app/modules/machine-inventory/utils/positionHistoryUtils';

export interface MachineDetailsPanelState {
  data: Optional<Machine>;
  isLoading: Optional<boolean>;
  isMetadataLoading: Optional<boolean>;
  isPictureLoading: Optional<boolean>;
  isTelemetryLoading: Optional<boolean>;
  isLatestCtrLoading: Optional<boolean>;
  isLatestRoutineLoading: Optional<boolean>;
  notifications: {
    data: Optional<Notification[]>;
    isLoading: Optional<boolean>;
    totalCount: Optional<number>;
    paginationTokens: PaginationTokens;
    page: number;
    pageSize: number;
    sortOptions: {
      field: Optional<string>;
      order: Optional<SortOrders>;
    };
  };
  operatingTime: {
    data: Optional<OperatingTimePeriod>;
    isLoading: Optional<boolean>;
  };
  reminders: {
    data: Optional<Reminder[]>;
    isInitialLoad: Optional<boolean>;
    isLoadingMore: Optional<boolean>;
    totalCount: Optional<number>;
    paginationTokens: PaginationTokens;
    page: number;
    pageSize: number;
  };
  notes: {
    data: Optional<MachineNote[]>;
    isInitialLoad: Optional<boolean>;
    isLoadingMore: Optional<boolean>;
    totalCount: Optional<number>;
    paginationTokens: PaginationTokens;
    page: number;
    pageSize: number;
  };
  attachments: {
    data: Optional<IMachineAttachment[]>;
    isLoading: Optional<boolean>;
    totalCount: Optional<number>;
    paginationTokens: PaginationTokens;
    page: number;
    pageSize: number;
    sortOptions: {
      field: Maybe<string>;
      order: Maybe<SortOrders>;
    };
    downloads: {
      isLoading: Optional<boolean>;
    };
  };
  updateMachineInfo: {
    data: Optional<MachineUpdateResponse>;
  };
  positionHistories: {
    data: Optional<EquipmentPositionSummary[]>;
    requestParams: Optional<MachineClientListPositionHistoriesOptions>;
    isLoading: Optional<boolean>;
    isLoadingMoreData: Optional<boolean>;
    nextPaginationToken: string | null;
    totalCount: Optional<number>;
  };
}

export const initialState: MachineDetailsPanelState = {
  data: null,
  isLoading: null,
  isMetadataLoading: null,
  isPictureLoading: true,
  isTelemetryLoading: true,
  isLatestRoutineLoading: true,
  isLatestCtrLoading: true,
  notifications: {
    data: null,
    totalCount: null,
    isLoading: null,
    paginationTokens: {},
    page: DEFAULT_PAGE_VALUE,
    pageSize: DEFAULT_MACHINE_NOTIFICATIONS_PAGE_SIZE_VALUE,
    sortOptions: {
      field: null,
      order: null,
    },
  },
  operatingTime: {
    data: null,
    isLoading: null,
  },
  reminders: {
    data: null,
    isInitialLoad: null,
    isLoadingMore: null,
    totalCount: null,
    paginationTokens: {},
    page: DEFAULT_PAGE_VALUE,
    pageSize: MACHINE_REMINDER_LIST_PAGE_SIZE,
  },
  notes: {
    data: null,
    isInitialLoad: null,
    isLoadingMore: null,
    totalCount: null,
    paginationTokens: {},
    page: DEFAULT_PAGE_VALUE,
    pageSize: MACHINE_REMINDER_LIST_PAGE_SIZE,
  },
  attachments: {
    data: null,
    totalCount: null,
    isLoading: null,
    paginationTokens: {},
    page: DEFAULT_PAGE_VALUE,
    pageSize: MACHINE_ATTACHMENT_LIST_PAGE_SIZE_LARGE,
    sortOptions: {
      field: null,
      order: null,
    },
    downloads: {
      isLoading: null,
    },
  },
  updateMachineInfo: {
    data: null,
  },
  positionHistories: {
    data: null,
    requestParams: null,
    isLoading: null,
    isLoadingMoreData: null,
    nextPaginationToken: null,
    totalCount: null,
  },
};

export const machineDetailsPanelReducer = (state = initialState, action: AnyAction): MachineDetailsPanelState =>
  produce(state, draft => {
    switch (action.type) {
      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_REQUEST: {
        draft.isLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_SUCCESS: {
        const {
          machine: { data },
        } = action.payload;

        draft.isLoading = false;
        draft.data = data;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_PICTURE_SUCCESS: {
        const {
          machine: { data: machineVariantDatum },
        } = action.payload;

        draft.isPictureLoading = false;

        if (!isNil(draft.data)) {
          draft.data = MachineUtils.mergeMachineWithVariantData(draft.data, [machineVariantDatum]);
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_TELEMETRIES_SUCCESS: {
        const {
          machine: { data: machineTelemetriesDatum },
        } = action.payload;

        draft.isTelemetryLoading = false;

        if (!isNil(draft.data)) {
          draft.data = MachineUtils.mergeMachineWithTelemetryData(draft.data, [machineTelemetriesDatum]);
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_LATEST_ROUTINE_SUCCESS: {
        const {
          machine: { data: machineLatestRoutineDatum },
        } = action.payload;

        draft.isLatestRoutineLoading = false;

        if (!isNil(draft.data)) {
          draft.data = MachineUtils.mergeMachineWithLatestRoutineData(draft.data, [machineLatestRoutineDatum]);
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_LATEST_CTR_SUCCESS: {
        const {
          machine: { data: machineLatestCtrDatum },
        } = action.payload;

        draft.isLatestCtrLoading = false;

        if (!isNil(draft.data)) {
          draft.data = MachineUtils.mergeMachineWithLatestCtrData(draft.data, [machineLatestCtrDatum]);
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_ERROR: {
        draft.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_OPERATING_TIME_REQUEST: {
        draft.operatingTime.isLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_OPERATING_TIME_SUCCESS: {
        const {
          machine: {
            data: { operatingTimeForPeriod },
          },
        } = action.payload;

        draft.operatingTime.isLoading = false;
        draft.operatingTime.data = operatingTimeForPeriod;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_OPERATING_TIME_ERROR: {
        draft.operatingTime.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_REMINDERS_REQUEST: {
        const { append } = action.payload;

        if (append) {
          draft.reminders.isLoadingMore = true;
        } else {
          draft.reminders.isInitialLoad = true;
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_REMINDERS_SUCCESS: {
        const {
          machine: {
            data: {
              reminders: {
                data,
                metadata: { totalCount, paginationToken },
              },
            },
          },
          append,
        } = action.payload;

        if (append) {
          draft.reminders.isLoadingMore = false;
          draft.reminders.page += 1;
          draft.reminders.data = (draft.reminders.data ?? []).concat(data);
        } else {
          draft.reminders.isInitialLoad = false;
          draft.reminders.page = 1;
          draft.reminders.data = data;
        }

        draft.reminders.totalCount = totalCount;
        if (paginationToken) {
          draft.reminders.paginationTokens[draft.reminders.page + 1] = paginationToken;
        }
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_REMINDERS_ERROR: {
        draft.reminders.isInitialLoad = false;
        draft.reminders.isLoadingMore = false;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTES_REQUEST: {
        const { append } = action.payload;

        if (append) {
          draft.notes.isLoadingMore = true;
        } else {
          draft.notes.isInitialLoad = true;
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTES_SUCCESS: {
        const {
          machine: {
            data: {
              notes: {
                data,
                metadata: { totalCount, paginationToken },
              },
            },
          },
          append,
        } = action.payload;

        if (append) {
          draft.notes.isLoadingMore = false;
          draft.notes.page += 1;
          draft.notes.data = (draft.notes.data ?? []).concat(data);
        } else {
          draft.notes.isInitialLoad = false;
          draft.notes.page = 1;
          draft.notes.data = data;
        }

        draft.notes.totalCount = totalCount;
        if (paginationToken) {
          draft.notes.paginationTokens[draft.notes.page + 1] = paginationToken;
        }
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTES_ERROR: {
        draft.notes.isInitialLoad = false;
        draft.notes.isLoadingMore = false;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_REQUEST: {
        draft.positionHistories.isLoading = true;
        draft.positionHistories.isLoadingMoreData = false;
        draft.positionHistories.nextPaginationToken = null;
        draft.positionHistories.data = null;
        draft.positionHistories.totalCount = null;
        draft.positionHistories.requestParams = action.payload;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_SUCCESS: {
        const { data, paginationToken, totalCount } = action.payload;

        draft.positionHistories.isLoading = false;
        draft.positionHistories.data = data;
        draft.positionHistories.totalCount = totalCount ?? null;
        draft.positionHistories.nextPaginationToken = getNextPaginationToken(paginationToken ?? '');

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_ERROR: {
        draft.positionHistories.isLoading = false;
        draft.positionHistories.data = [];
        draft.positionHistories.nextPaginationToken = null;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_MORE_DATA_REQUEST: {
        draft.positionHistories.isLoadingMoreData = true;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_MORE_DATA_SUCCESS: {
        const { data, paginationToken } = action.payload;

        draft.positionHistories.isLoadingMoreData = false;
        draft.positionHistories.data = sortPositionSummariesByEnteredAtDesc(
          (draft.positionHistories.data ?? []).concat(data)
        );
        draft.positionHistories.nextPaginationToken = getNextPaginationToken(paginationToken ?? '');

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_POSITION_HISTORIES_MORE_DATA_ERROR: {
        draft.positionHistories.isLoadingMoreData = false;
        return draft;
      }

      case MachineDetailsPanelActions.UPDATE_MACHINE_SUCCESS: {
        const {
          machineUpdate: { data },
        } = action.payload;

        if (!isNil(draft.data)) {
          draft.data.name = data.name;
        }

        return draft;
      }

      case MachineDetailsPanelActions.UPDATE_MACHINE_INFO_SUCCESS: {
        draft.updateMachineInfo.data = action.payload?.machineUpdate?.data;

        Object.assign(draft.data?.metadata || {}, action.payload?.machineUpdate?.data.metadata);
        if (action.payload?.machineUpdate?.data.site) {
          Object.assign(draft.data || {}, { site: action.payload?.machineUpdate?.data.site });
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTIFICATION_LIST_REQUEST: {
        draft.notifications.isLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTIFICATION_LIST_SUCCESS: {
        const {
          notifications: {
            data,
            metadata: { totalCount, paginationToken },
          },
        } = action.payload;
        draft.notifications.isLoading = false;
        draft.notifications.data = data;
        draft.notifications.totalCount = totalCount;
        if (paginationToken) {
          draft.notifications.paginationTokens[draft.notifications.page + 1] = paginationToken;
        }
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_NOTIFICATION_LIST_ERROR: {
        draft.notifications.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.CHANGE_NOTIFICATIONS_TABLE_PAGE: {
        const { page } = action.payload;
        draft.notifications.page = page;
        return draft;
      }

      case MachineDetailsPanelActions.SET_ACTIVE_NOTIFICATIONS_SORT_FIELD: {
        const { field } = action.payload;
        draft.notifications.sortOptions.field = field;
        return draft;
      }

      case MachineDetailsPanelActions.SET_ACTIVE_NOTIFICATIONS_SORT_ORDER: {
        const { order } = action.payload;
        draft.notifications.sortOptions.order = order;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_ATTACHMENTS_LIST_REQUEST: {
        draft.attachments.isLoading = true;
        return draft;
      }
      case MachineDetailsPanelActions.GET_MACHINE_ATTACHMENTS_LIST_SUCCESS: {
        const {
          machine: {
            data: {
              attachments: { data, metadata },
            },
          },
        } = action.payload as MachineAttachmentsListResponse;
        draft.attachments.isLoading = false;
        draft.attachments.data = data.map(datum => ({
          id: datum.id,
          fileName: datum.name,
          mimeType: datum.mimeType,
          createdAt: datum.createdAt,
        }));

        draft.attachments.totalCount = metadata?.totalCount;
        if (metadata?.paginationToken) {
          draft.attachments.paginationTokens[draft.attachments.page + 1] = metadata?.paginationToken;
        }
        return draft;
      }
      case MachineDetailsPanelActions.GET_MACHINE_ATTACHMENTS_LIST_ERROR: {
        draft.attachments.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.MACHINE_ATTACHMENTS_IS_LOADING: {
        draft.attachments.isLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.CHANGE_MACHINE_ATTACHMENTS_TABLE_PAGE: {
        const { page } = action.payload;
        draft.attachments.page = page;
        return draft;
      }

      case MachineDetailsPanelActions.SET_MACHINE_ATTACHMENT_ACTIVE_SORT_ORDER: {
        const { order } = action.payload;
        draft.attachments.sortOptions.order = order;
        draft.attachments.paginationTokens = {};
        draft.attachments.page = DEFAULT_PAGE_VALUE;
        return draft;
      }

      case MachineDetailsPanelActions.DOWNLOAD_MACHINE_ATTACHMENT_REQUEST: {
        draft.attachments.downloads.isLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.DOWNLOAD_MACHINE_ATTACHMENT_SUCCESS: {
        draft.attachments.downloads.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.DOWNLOAD_MACHINE_ATTACHMENT_ERROR: {
        draft.attachments.downloads.isLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_METADATA_REQUEST: {
        draft.isMetadataLoading = true;
        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_METADATA_SUCCESS: {
        const {
          machine: { data },
        } = action.payload;

        draft.isMetadataLoading = false;

        if (!isNil(draft.data)) {
          draft.data = {
            ...draft.data,
            metadata: data.metadata,
          };
        }

        return draft;
      }

      case MachineDetailsPanelActions.GET_MACHINE_DETAILS_METADATA_ERROR: {
        draft.isMetadataLoading = false;
        return draft;
      }

      case MachineDetailsPanelActions.RESET_STATE: {
        return initialState;
      }

      case MachineDetailsPanelActions.MACHINE_DETAILS_ROBOT_REAL_TIME_PROPERTY_CHANGED: {
        if (draft.data && action.payload.updatedData) {
          draft.data = RobotUtils.getRobotPropertiesUpdated(draft.data, action.payload.updatedData);

          return draft;
        }

        return draft;
      }

      case MachineDetailsPanelActions.MACHINE_DETAILS_ROBOT_REAL_TIME_ROUTINE_CHANGED: {
        if (draft.data && action.payload.updatedData) {
          draft.data = RobotUtils.getRobotRoutineUpdated(draft.data, action.payload.updatedData.data);

          return draft;
        }

        return draft;
      }

      default:
        return draft;
    }
  });
